export type CreditScoreBand = {
  label: string;
  minScore: number;
  maxScore: number;
  /**
   * Added to the base rate (in percentage points).
   * Example: +0.55 means baseRatePct + 0.55.
   */
  rateAdjustmentPct: number;
};

// Simple, transparent credit-score adjustment model. This is intentionally editable
// in the UI by overriding the final interest rate.
export const CREDIT_SCORE_BANDS: CreditScoreBand[] = [
  { label: "760-850", minScore: 760, maxScore: 850, rateAdjustmentPct: -0.35 },
  { label: "740-759", minScore: 740, maxScore: 759, rateAdjustmentPct: -0.2 },
  { label: "720-739", minScore: 720, maxScore: 739, rateAdjustmentPct: -0.05 },
  { label: "700-719", minScore: 700, maxScore: 719, rateAdjustmentPct: 0.1 },
  { label: "680-699", minScore: 680, maxScore: 699, rateAdjustmentPct: 0.3 },
  { label: "660-679", minScore: 660, maxScore: 679, rateAdjustmentPct: 0.55 },
  { label: "640-659", minScore: 640, maxScore: 659, rateAdjustmentPct: 0.85 },
  { label: "620-639", minScore: 620, maxScore: 639, rateAdjustmentPct: 1.2 },
  { label: "300-619", minScore: 300, maxScore: 619, rateAdjustmentPct: 1.75 },
];

export const clampNumber = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const getCreditScoreBand = (creditScore: number): CreditScoreBand => {
  const score = Math.round(clampNumber(creditScore, 300, 850));
  return (
    CREDIT_SCORE_BANDS.find(
      (b) => score >= b.minScore && score <= b.maxScore
    ) || CREDIT_SCORE_BANDS[CREDIT_SCORE_BANDS.length - 1]
  );
};

export const getRateAdjustmentPctForCreditScore = (
  creditScore: number
): number => getCreditScoreBand(creditScore).rateAdjustmentPct;

export const computeDownPaymentAmount = (
  homePrice: number,
  downPaymentPct: number
): number => {
  const price = Number.isFinite(homePrice) ? Math.max(0, homePrice) : 0;
  const pct = clampNumber(downPaymentPct, 0, 100) / 100;
  return price * pct;
};

export const computeLoanAmount = (
  homePrice: number,
  downPaymentAmount: number
): number => {
  const price = Number.isFinite(homePrice) ? Math.max(0, homePrice) : 0;
  const down = Number.isFinite(downPaymentAmount)
    ? Math.max(0, downPaymentAmount)
    : 0;
  return Math.max(0, price - down);
};

export const computeMonthlyMortgagePayment = (params: {
  principal: number;
  annualRatePct: number;
  termYears: number;
}): number => {
  const principal = Number.isFinite(params.principal)
    ? Math.max(0, params.principal)
    : 0;
  const termYears = Number.isFinite(params.termYears)
    ? Math.max(0, params.termYears)
    : 0;
  const months = Math.max(0, Math.round(termYears * 12));
  if (principal <= 0 || months <= 0) return 0;

  const monthlyRate = Number.isFinite(params.annualRatePct)
    ? params.annualRatePct / 100 / 12
    : 0;
  if (!Number.isFinite(monthlyRate) || monthlyRate <= 0) {
    return principal / months;
  }

  // payment = P * r / (1 - (1+r)^-n)
  const pow = Math.pow(1 + monthlyRate, -months);
  const denom = 1 - pow;
  if (!Number.isFinite(denom) || denom <= 0) return 0;
  const payment = principal * (monthlyRate / denom);
  return Number.isFinite(payment) ? payment : 0;
};

export type AmortizationRow = {
  month: number; // 1-indexed
  payment: number;
  interest: number;
  principal: number;
  balance: number;
};

export const buildAmortizationSchedule = (params: {
  principal: number;
  annualRatePct: number;
  termYears: number;
  months: number;
}): AmortizationRow[] => {
  const principal = Number.isFinite(params.principal)
    ? Math.max(0, params.principal)
    : 0;
  const termYears = Number.isFinite(params.termYears)
    ? Math.max(0, params.termYears)
    : 0;
  const monthsTotal = Math.max(0, Math.round(termYears * 12));
  const monthsToBuild = Math.max(0, Math.min(params.months, monthsTotal));
  if (principal <= 0 || monthsTotal <= 0 || monthsToBuild <= 0) return [];

  const payment = computeMonthlyMortgagePayment({
    principal,
    annualRatePct: params.annualRatePct,
    termYears,
  });
  const monthlyRate = Number.isFinite(params.annualRatePct)
    ? params.annualRatePct / 100 / 12
    : 0;

  const rows: AmortizationRow[] = [];
  let balance = principal;
  for (let m = 1; m <= monthsToBuild; m++) {
    const interest =
      Number.isFinite(monthlyRate) && monthlyRate > 0
        ? balance * monthlyRate
        : 0;
    const principalPaid = Math.max(0, Math.min(balance, payment - interest));
    const nextBalance = Math.max(0, balance - principalPaid);
    rows.push({
      month: m,
      payment,
      interest,
      principal: principalPaid,
      balance: nextBalance,
    });
    balance = nextBalance;
    if (balance <= 0) break;
  }

  return rows;
};

export const sumAmortizationInterest = (rows: AmortizationRow[]): number =>
  rows.reduce(
    (sum, row) => sum + (Number.isFinite(row.interest) ? row.interest : 0),
    0
  );

export const computeAnnualPropertyTax = (
  homePrice: number,
  propertyTaxRatePct: number
): number => {
  const price = Number.isFinite(homePrice) ? Math.max(0, homePrice) : 0;
  const ratePct = Number.isFinite(propertyTaxRatePct)
    ? clampNumber(propertyTaxRatePct, 0, 100)
    : 0;
  return price * (ratePct / 100);
};

export const computeMonthlyFromAnnual = (annual: number): number => {
  const value = Number.isFinite(annual) ? annual : 0;
  return value / 12;
};

export type MortgageTaxSettings = {
  federalMarginalRate: number; // 0..1
  stateMarginalRate: number; // 0..1
  saltCap: number; // annual, USD
  otherSaltUsed: number; // annual, USD
  mortgageInterestDebtLimit: number; // USD (principal cap)
};

export type MortgageTaxImpact = {
  firstYearInterest: number;
  annualPropertyTax: number;
  federalDeductibleInterest: number;
  federalDeductiblePropertyTax: number;
  federalTaxSavings: number;
  stateDeductibleInterest: number;
  stateDeductiblePropertyTax: number;
  stateTaxSavings: number;
  totalAnnualTaxSavings: number;
};

export const computeMortgageTaxImpact = (params: {
  loanAmount: number;
  firstYearInterest: number;
  annualPropertyTax: number;
  settings: MortgageTaxSettings;
}): MortgageTaxImpact => {
  const loanAmount = Number.isFinite(params.loanAmount)
    ? Math.max(0, params.loanAmount)
    : 0;
  const firstYearInterest = Number.isFinite(params.firstYearInterest)
    ? Math.max(0, params.firstYearInterest)
    : 0;
  const annualPropertyTax = Number.isFinite(params.annualPropertyTax)
    ? Math.max(0, params.annualPropertyTax)
    : 0;

  const federalMarginalRate = clampNumber(
    Number.isFinite(params.settings.federalMarginalRate)
      ? params.settings.federalMarginalRate
      : 0,
    0,
    1
  );
  const stateMarginalRate = clampNumber(
    Number.isFinite(params.settings.stateMarginalRate)
      ? params.settings.stateMarginalRate
      : 0,
    0,
    1
  );
  const saltCap = Number.isFinite(params.settings.saltCap)
    ? Math.max(0, params.settings.saltCap)
    : 0;
  const otherSaltUsed = Number.isFinite(params.settings.otherSaltUsed)
    ? Math.max(0, params.settings.otherSaltUsed)
    : 0;
  const mortgageInterestDebtLimit = Number.isFinite(
    params.settings.mortgageInterestDebtLimit
  )
    ? Math.max(0, params.settings.mortgageInterestDebtLimit)
    : 0;

  const interestDeductionFactor =
    mortgageInterestDebtLimit > 0 && loanAmount > mortgageInterestDebtLimit
      ? mortgageInterestDebtLimit / loanAmount
      : 1;
  const deductibleInterest = firstYearInterest * interestDeductionFactor;

  const remainingSalt = Math.max(0, saltCap - otherSaltUsed);
  const deductiblePropertyTaxFederal = Math.min(
    annualPropertyTax,
    remainingSalt
  );

  const federalTaxSavings =
    (deductibleInterest + deductiblePropertyTaxFederal) * federalMarginalRate;

  // Oregon specifics can vary; we model state savings independently without a SALT cap.
  const deductiblePropertyTaxState = annualPropertyTax;
  const stateTaxSavings =
    (deductibleInterest + deductiblePropertyTaxState) * stateMarginalRate;

  const totalAnnualTaxSavings = federalTaxSavings + stateTaxSavings;

  return {
    firstYearInterest,
    annualPropertyTax,
    federalDeductibleInterest: deductibleInterest,
    federalDeductiblePropertyTax: deductiblePropertyTaxFederal,
    federalTaxSavings,
    stateDeductibleInterest: deductibleInterest,
    stateDeductiblePropertyTax: deductiblePropertyTaxState,
    stateTaxSavings,
    totalAnnualTaxSavings,
  };
};
